import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { setSessionToken, getSessionToken } from '../lib/authToken';
import { Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing (React 18 StrictMode mounts twice in dev)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const hash = location.hash || window.location.hash;
        console.log('[AUTH-CB] Processing callback, hash present:', !!hash);

        // New Google OAuth flow: session_token directly in hash
        const sessionTokenMatch = hash.match(/session_token=([^&]+)/);
        if (sessionTokenMatch) {
          const sessionToken = decodeURIComponent(sessionTokenMatch[1]);
          console.log('[AUTH-CB] Direct session_token received (len=' + sessionToken.length + ')');
          setSessionToken(sessionToken);
          try { window.history.replaceState(null, '', '/'); } catch (_) {}
          const me = await refreshUser();
          if (!me) {
            setError('Could not verify session. Please try logging in again.');
            return;
          }
          const needsOnboarding = hash.includes('needs_onboarding=true');
          navigate(needsOnboarding ? '/onboarding' : '/dashboard', { replace: true });
          return;
        }

        const sessionIdMatch = hash.match(/session_id=([^&]+)/);

        // No session_id or session_token in URL
        if (!sessionIdMatch) {
          console.warn('[AUTH-CB] No session_id or session_token in hash');
          if (getSessionToken()) {
            const u = await refreshUser();
            navigate(u ? '/dashboard' : '/', { replace: true });
          } else {
            navigate('/', { replace: true });
          }
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log('[AUTH-CB] Exchanging session_id (len=' + sessionId.length + ')');

        // Exchange session_id for app session_token (legacy Emergent flow)
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        const payload = response.data || {};
        console.log('[AUTH-CB] Exchange OK; needs_onboarding=' + !!payload.needs_onboarding);

        if (!payload.session_token) {
          throw new Error('Auth server response missing session_token');
        }
        setSessionToken(payload.session_token);

        try { window.history.replaceState(null, '', '/'); } catch (_) {}

        const me = await refreshUser();
        if (!me) {
          console.error('[AUTH-CB] /me returned null right after session exchange');
          setError('Could not verify session. Please try logging in again.');
          return;
        }

        const destination = payload.needs_onboarding ? '/onboarding' : '/dashboard';
        console.log('[AUTH-CB] Navigating to', destination);
        navigate(destination, { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || 'Unknown error';
        console.error('[AUTH-CB] Auth exchange failed:', detail);
        setError(detail);
      }
    };

    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="text-center max-w-md p-6">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white text-lg font-medium mb-2">Login failed</p>
          <p className="text-slate-400 text-sm mb-6">{error}</p>
          <button
            onClick={() => (window.location.href = '/')}
            className="px-4 py-2 bg-[#00E599] text-black rounded-sm font-medium hover:bg-[#00CC88]"
            data-testid="auth-error-back-btn"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#00E599] animate-spin mx-auto mb-4" />
        <p className="text-white text-lg font-medium">Signing you in...</p>
        <p className="text-slate-400 text-sm mt-2">Please wait while we verify your account</p>
      </div>
    </div>
  );
}
